// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { theme } from "@insite/client-framework/Theme";
import { getProp } from "@insite/mobius/utilities";
import { autoClamp, fontConfig } from "@vanmeter/XDL/constants";
import { accordionStyles, linkTypoStyles, specialClassesStyles, tableStyles } from "@vanmeter/XDL/PreStyle/globalStyle";
import { xBaseTheme } from "@vanmeter/XDL/types";
import get from "lodash/get";
import { css } from "styled-components";

export default {
    fontFamilyImportUrl: fontConfig.url,
    body: {
        size: `${fontConfig.regular.size}px`,
        weight: fontConfig.weight.regular,
        fontFamily: fontConfig.fontFamily,
        color: "text",
        css: css`
            transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
        `,
    },
    p: {
        ...fontConfig.regular,
        weight: fontConfig.weight.regular,
        css: css`
            margin-bottom: 16px;
            letter-spacing: 0;
            display: block;
        `,
    },
    h1: {
        size: autoClamp(32, 32, 44),
        weight: fontConfig.weight.bold,
        lineHeight: autoClamp(36, 36, 50),
        fontFamily: fontConfig.secondaryFontFamily,
        color: "common.ui7",
        css: css`
            margin-bottom: 20px;
            letter-spacing: 0;
            display: block;
        `,
    },
    h2: {
        size: autoClamp(28, 28, 32),
        weight: fontConfig.weight.bold,
        lineHeight: autoClamp(32, 32, 36),
        fontFamily: fontConfig.secondaryFontFamily,
        color: "common.ui7",
        css: css`
            margin-bottom: 16px;
            letter-spacing: 0;
            display: block;
        `,
    },
    h3: {
        size: autoClamp(24, 24, 28),
        weight: fontConfig.weight.bold,
        lineHeight: autoClamp(27, 27, 32),
        fontFamily: fontConfig.secondaryFontFamily,
        color: "common.ui7",
        css: css`
            margin-bottom: 12px;
            letter-spacing: 0;
            display: block;
        `,
    },
    h4: {
        size: autoClamp(20, 20, 24),
        weight: fontConfig.weight.bold,
        lineHeight: autoClamp(23, 23, 27),
        fontFamily: fontConfig.secondaryFontFamily,
        color: "common.ui7",
        css: css`
            margin-bottom: 12px;
            letter-spacing: 0;
            display: block;
        `,
    },
    h5: {
        size: "18px",
        weight: fontConfig.weight.bold,
        lineHeight: "21px",
        fontFamily: fontConfig.secondaryFontFamily,
        color: "common.ui7",
        css: css`
            margin-bottom: 8px;
            letter-spacing: 0;
            display: block;
        `,
    },
    h6: {
        size: "16px",
        weight: fontConfig.weight.bold,
        lineHeight: "18px",
        fontFamily: fontConfig.secondaryFontFamily,
        color: "common.ui7",
        css: css`
            margin-bottom: 8px;
            letter-spacing: 0;
            display: block;
        `,
    },
    headerPrimary: {
        size: autoClamp(52, 52, 64),
        weight: fontConfig.weight.bold,
        lineHeight: autoClamp(52, 52, 64),
        fontFamily: fontConfig.secondaryFontFamily,
        color: "common.ui7",
        transform: "uppercase",
        as: "h1",
        css: css`
            margin-bottom: 20px;
            letter-spacing: 0;
            display: block;
        `,
    },
    headerSecondary: {
        size: autoClamp(40, 40, 48),
        weight: fontConfig.weight.bold,
        lineHeight: autoClamp(40, 40, 48),
        fontFamily: fontConfig.secondaryFontFamily,
        color: "common.ui7",
        transform: "uppercase",
        as: "h1",
        css: css`
            margin-bottom: 16px;
            letter-spacing: 0;
            display: block;
        `,
    },
    headerTertiary: {
        forwardAs: "p",
        size: "18px",
        weight: fontConfig.weight.regular,
        fontFamily: fontConfig.fontFamily,
        color: "text",
        transform: "uppercase",
    },
    legend: {
        forwardAs: "span",
        size: "10px",
        color: "text",
        transform: "uppercase",
        weight: fontConfig.weight.regular,
    },
    span: {
        size: "inherit",
        lineHeight: "inherit",
        weight: "inherit",
        css: css`
            letter-spacing: 0;
        `,
    },
    // styling CMS rich content
    "&&& div.fr-wrapper": {
        css: css`
            ${tableStyles}
            ${specialClassesStyles}
            ${linkTypoStyles}
            .as-white {
                text-shadow: 0 0 6px rgba(0, 0, 0, 0.6);
            }
            ${accordionStyles}
            .fr-accordion {
                box-shadow: ${get(theme, "shadows.1")};
            }
        `,
    },
} as xBaseTheme["typography"];
